import { useRef, useState } from 'react';
import './gtm.css';

const CALENDLY = 'https://calendly.com/sanshalabs';

const STEPS = [
  { id: 'read', label: 'Reading your website and public mentions' },
  { id: 'find', label: 'Finding the top players in your space' },
  { id: 'scan', label: 'Researching each competitor' },
  { id: 'goal', label: 'Building your SWOT and reading your goal' },
];

const INDUSTRIES = [
  'B2B SaaS / software',
  'AI products',
  'Media, film & entertainment tech',
  'Events, music & live entertainment',
  'Fintech',
  'Healthtech',
  'Edtech',
  'E-commerce & D2C brands',
  'Marketplaces',
  'Travel & hospitality',
  'Food & beverage',
  'Real estate & proptech',
  'Logistics & mobility',
  'HR & recruitment',
  'Marketing & creative agencies',
  'Consulting & professional services',
  'Manufacturing & hardware',
];
const OTHER = 'Other';

// "try an example" cycles through well-known companies with an India growth goal.
const EXAMPLES = [
  { company: 'Zepto', industry: 'E-commerce & D2C brands', website: 'zeptonow.com', market: 'India', goal: 'Grow orders in Tier-2 cities' },
  { company: 'Sarvam AI', industry: 'AI products', website: 'sarvam.ai', market: 'India', goal: 'Win Indian banks for regional-language customer support' },
  { company: 'Higgsfield', industry: 'AI products', website: 'higgsfield.ai', market: 'India', goal: 'Win Indian ad agencies as resellers' },
];

const EMPTY = { company: '', industry: '', website: '', market: '', goal: '' };

const FULL_STRATEGY = [
  ['Beachhead & ideal customer', 'The one segment to win first, and who signs the deal'],
  ['Positioning & differentiation', 'Why buyers should choose you over what they use today'],
  ['Pricing & packaging', 'Priced on the value your buyer actually measures'],
  ['Sales motion & channels', 'Founder-led, partner-led or self-serve: the motion your deal size can support'],
  ['Win/loss & message testing', 'Real buyer conversations to test what lands before you scale'],
  ['90-day launch & pipeline targets', 'Weekly targets from first meeting to closed deal'],
];

const NOT_LISTED = 'Not on their site';
const NO_ACCESS = "Couldn't access";
const GAPS = [NOT_LISTED, NO_ACCESS, 'Not published'];
const isGap = (v) => !v || GAPS.includes(v);

function hostOf(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return 'source'; }
}
const shortUrl = (u) => u.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
const has = (v) => !isGap(v) && (!Array.isArray(v) || v.length > 0);

function SourceList({ sources }) {
  const [open, setOpen] = useState(false);
  if (!sources?.length) return null;
  const shown = open ? sources : sources.slice(0, 5);
  return (
    <div className="gtm-src-line">
      <span className="gtm-src-label">Sources</span>
      {shown.map((src, i) => (
        <a key={i} href={src.url} target="_blank" rel="noopener noreferrer nofollow" title={`${src.kind === 'cited' ? 'Cited' : 'Read'}: ${src.title || src.url}`}>
          {shortUrl(src.url)}
        </a>
      ))}
      {sources.length > 5 && (
        <button type="button" onClick={() => setOpen((v) => !v)}>{open ? 'less' : `+${sources.length - 5} more`}</button>
      )}
    </div>
  );
}

function signals(sc) {
  const lvl = (n, hi, mid, lo) => (n >= 4 ? hi : n === 3 ? mid : lo);
  return [
    lvl(sc.goalFit, 'Strong goal fit', 'Partial goal fit', 'Weak goal fit'),
    lvl(sc.evidence, 'Strong evidence', 'Some evidence', 'Thin evidence'),
    lvl(sc.gap, 'Open gap', 'Partly covered', 'Crowded'),
    lvl(sc.speed, 'Quick to act', 'Medium effort', 'Longer build'),
  ];
}

// A fact is either {value, detail} (current) or a plain string (older answers).
function Fact({ label, fact }) {
  const value = fact && typeof fact === 'object' ? fact.value : fact;
  const detail = fact && typeof fact === 'object' ? fact.detail : null;
  return (
    <div className="gtm-fact">
      <div className="gtm-fact-label">{label}</div>
      <div className={`gtm-fact-value ${has(value) ? '' : 'is-np'}`}>{has(value) ? value : (value === NO_ACCESS ? NO_ACCESS : NOT_LISTED)}</div>
      {has(value) && has(detail) && <div className="gtm-fact-detail">{detail}</div>}
    </div>
  );
}

export default function GtmPage() {
  const [form, setForm] = useState(EMPTY);
  const [industryChoice, setIndustryChoice] = useState('');
  const [exampleIdx, setExampleIdx] = useState(0);
  const [phase, setPhase] = useState('idle'); // idle | running | done
  const [steps, setSteps] = useState({});
  const [result, setResult] = useState({});
  const [error, setError] = useState('');
  const [showAll, setShowAll] = useState(false);
  const resultsRef = useRef(null);

  const busy = phase === 'running';
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const canRun = form.company && form.industry && form.website && form.goal && !busy;

  function chooseIndustry(e) {
    const v = e.target.value;
    setIndustryChoice(v);
    setForm((f) => ({ ...f, industry: v === OTHER ? '' : v }));
  }

  function useExample() {
    const ex = EXAMPLES[exampleIdx % EXAMPLES.length];
    setForm(ex);
    setIndustryChoice(ex.industry);
    setExampleIdx((i) => i + 1);
  }

  async function run(e) {
    e.preventDefault();
    if (!canRun) return;
    setPhase('running');
    setSteps({});
    setResult({});
    setError('');
    setShowAll(false);
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);

    try {
      const res = await fetch('/api/gtm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok || !res.body) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'The agent is unavailable right now. Please try again shortly.');
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop();
        for (const line of lines) {
          if (!line.trim()) continue;
          let msg;
          try { msg = JSON.parse(line); } catch { continue; }
          if (msg.type === 'step') setSteps((s) => ({ ...s, [msg.id]: msg }));
          else if (msg.type === 'result') setResult((r) => ({ ...r, [msg.key]: msg.data }));
          else if (msg.type === 'error') setError(msg.message);
        }
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setPhase('done');
    }
  }

  const started = phase !== 'idle';
  const { profile, competitors, goal, swot, marketSources } = result;
  const facts = profile?.facts || profile || {};
  const traction = (profile?.traction || []).filter((h) => !isGap(h));
  const sources = profile?.sources?.length
    ? profile.sources
    : (profile?.pagesRead || []).filter((u) => /^https?:\/\//.test(u)).map((url) => ({ url, kind: 'read' }));

  return (
    <div className="gtm-page">
      <div className="gtm-stars" />
      <nav className="gtm-nav">
        <a href="/" aria-label="Sansha Labs home"><img src="/sansha-logo.png" alt="Sansha Labs" /></a>
        <div className="gtm-nav-links">
          <a href="/">Home</a>
          <span className="is-here">GTM Agent</span>
        </div>
      </nav>

      <main className="gtm-main">
        <div className="gtm-eyebrow">GTM Agent</div>
        <h1 className="gtm-h1">See your market the way a strategist would, in about two minutes.</h1>
        <p className="gtm-sub">
          The agent analyses your company, your market and each competitor, then presents a SWOT and a win analysis ranked by evidence.
        </p>
        <div className="gtm-pill-wrap"><span className="gtm-pill">A first look, from public information</span></div>

        <form className="gtm-card" onSubmit={run}>
          <div className="gtm-form">
            <div>
              <label className="gtm-label" htmlFor="g-company">Company name</label>
              <input id="g-company" className="gtm-input" value={form.company} onChange={set('company')} placeholder="Your company name" maxLength={80} disabled={busy} />
            </div>
            <div>
              <label className="gtm-label" htmlFor="g-industry">Industry</label>
              <select id="g-industry" className="gtm-input gtm-select" value={industryChoice} onChange={chooseIndustry} disabled={busy}>
                <option value="" disabled>Choose the closest</option>
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                <option value={OTHER}>Other…</option>
              </select>
              {industryChoice === OTHER && (
                <input className="gtm-input" style={{ marginTop: 8 }} value={form.industry} onChange={set('industry')} placeholder="Type your industry" maxLength={80} disabled={busy} autoFocus />
              )}
            </div>
            <div>
              <label className="gtm-label" htmlFor="g-website">Website</label>
              <input id="g-website" className="gtm-input" value={form.website} onChange={set('website')} placeholder="yourcompany.com" maxLength={200} inputMode="url" disabled={busy} />
            </div>
            <div>
              <label className="gtm-label" htmlFor="g-market">Market <span>(optional)</span></label>
              <input id="g-market" className="gtm-input" value={form.market} onChange={set('market')} placeholder="e.g. India, US, Global" maxLength={60} disabled={busy} />
            </div>
            <div className="full">
              <label className="gtm-label" htmlFor="g-goal">Your goal</label>
              <input id="g-goal" className="gtm-input" value={form.goal} onChange={set('goal')} placeholder="e.g. Win our first 50 paying customers in 6 months" maxLength={200} disabled={busy} />
            </div>
          </div>
          <div className="gtm-row">
            <span className="gtm-note">
              2 free runs per day · about two minutes ·{' '}
              <button type="button" onClick={useExample} disabled={busy}>try an example</button>
            </span>
            <button className="gtm-btn" type="submit" disabled={!canRun}>
              {busy ? 'Agent is working…' : 'Run the agent →'}
            </button>
          </div>
        </form>

        <div ref={resultsRef} />
        {started && (
          <>
            <div className="gtm-sec">Agent at work</div>
            <div className="gtm-card">
              <ul className="gtm-steps">
                {STEPS.map((s) => {
                  const st = steps[s.id];
                  const status = st?.status || 'pending';
                  return (
                    <li key={s.id}>
                      <i className={`gtm-dot ${status}`} aria-hidden="true" />
                      <div>
                        <div className="gtm-st" style={{ color: status === 'pending' ? 'var(--g-dim)' : undefined }}>
                          {s.label}{status === 'running' ? '…' : ''}
                        </div>
                        {st?.finding && <div className="gtm-find">{st.finding}</div>}
                        {st?.debug && <div className="gtm-debug">Preview only · {st.debug}</div>}
                      </div>
                    </li>
                  );
                })}
              </ul>
              {error && <div className="gtm-error">{error}</div>}
            </div>

            {(profile || competitors || goal || busy) && <div className="gtm-sec">Your first look</div>}

            {profile ? (
              <div className="gtm-card gtm-fade">
                <h3 className="gtm-h3">Company profile</h3>
                <p className="gtm-p gtm-lead">{profile.snapshot}</p>
                {has(profile.valueProp) && (
                  <div className="gtm-vp"><span>Core value proposition</span>{profile.valueProp}</div>
                )}
                <div className="gtm-facts">
                  <Fact label="Business model & pricing" fact={facts.businessModel} />
                  <Fact label="Stage & history" fact={facts.stage} />
                  <Fact label="Where they operate" fact={facts.geography} />
                  <Fact label="Who they sell to" fact={facts.audience} />
                </div>
                {has(profile.offerings) && (
                  <div className="gtm-block">
                    <div className="gtm-fact-label">Products and services</div>
                    <div className="gtm-chips">{profile.offerings.slice(0, 6).map((o, i) => <span key={i}>{o}</span>)}</div>
                  </div>
                )}
                <div className="gtm-block">
                  <div className="gtm-fact-label">Traction & proof</div>
                  {traction.length > 0 ? (
                    <>
                      <ul className="gtm-list">{(showAll ? traction : traction.slice(0, 3)).map((n, i) => <li key={i}>{n}</li>)}</ul>
                      {traction.length > 3 && (
                        <button type="button" className="gtm-more-btn" onClick={() => setShowAll((v) => !v)}>
                          {showAll ? 'Show less' : `Show ${traction.length - 3} more`}
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="gtm-fact-value is-np" style={{ fontWeight: 400 }}>No public proof points found</div>
                  )}
                </div>
                <SourceList sources={sources} />
              </div>
            ) : busy && (
              <div className="gtm-card"><div className="gtm-skel" style={{ width: '40%' }} /><div className="gtm-skel" /><div className="gtm-skel" style={{ width: '80%' }} /></div>
            )}

            {competitors?.length > 0 && (
              <div className="gtm-card gtm-fade">
                <h3 className="gtm-h3">Top players in your space</h3>
                <div className="gtm-tw">
                  <table className="gtm-table">
                    <thead>
                      <tr><th>Company</th><th>What they do</th><th>Who they sell to</th><th>Pricing</th><th>Standout</th></tr>
                    </thead>
                    <tbody>
                      {competitors.map((c, i) => (
                        <tr key={i}>
                          <td>
                            <span>
                              {c.name}
                              {c.source && /^https?:\/\//.test(c.source) && (
                                <><br /><a href={c.source} target="_blank" rel="noopener noreferrer nofollow">{hostOf(c.source)} ↗</a></>
                              )}
                            </span>
                          </td>
                          <td data-label="What they do" className={isGap(c.whatTheyDo) ? 'is-np' : ''}>{c.whatTheyDo}</td>
                          <td data-label="Who they sell to" className={isGap(c.targets) ? 'is-np' : ''}>{c.targets}</td>
                          <td data-label="Pricing" className={isGap(c.pricing) ? 'is-np' : ''}>{c.pricing}</td>
                          <td data-label="Standout" className={isGap(c.notable) ? 'is-np' : ''}>{c.notable}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <SourceList sources={marketSources} />
              </div>
            )}

            {swot && (
              <div className="gtm-card gtm-fade">
                <h3 className="gtm-h3">Outside-in SWOT</h3>
                <div className="gtm-swot">
                  {[['Strengths', swot.strengths], ['Weaknesses', swot.weaknesses], ['Opportunities', swot.opportunities], ['Threats', swot.threats]].map(([t, items]) => (
                    <div key={t}><b>{t}</b><ul>{(items || []).slice(0, 2).map((x, i) => <li key={i}>{x}</li>)}</ul></div>
                  ))}
                </div>
              </div>
            )}

            {goal?.openings?.length > 0 && (
              <div className="gtm-card gtm-fade">
                <h3 className="gtm-h3">Where you can win</h3>
                <p className="gtm-goal-q">“{goal.goal}”</p>
                <ol className="gtm-ranked">
                  {goal.openings.map((o, i) => (
                    <li key={i}>
                      <span className="gtm-rank">{i + 1}</span>
                      <div>
                        <b>{o.title}</b>
                        <span className="gtm-why">{o.why}</span>
                        {o.evidence && <span className="gtm-evidence">Based on: {o.evidence}</span>}
                        {o.scores && <span className="gtm-signals">{signals(o.scores).join(' · ')}</span>}
                      </div>
                    </li>
                  ))}
                </ol>
                <div className="gtm-goal-grid">
                  {has(goal.hurdle) && <div><div className="gtm-fact-label">Biggest hurdle</div><div className="gtm-fact-value">{goal.hurdle}</div></div>}
                  {has(goal.firstMove) && <div><div className="gtm-fact-label">First move (for #1)</div><div className="gtm-fact-value">{goal.firstMove}</div></div>}
                </div>
                <p className="gtm-rank-note">
                  Ranked{goal.considered > 3 ? ` from ${goal.considered} options` : ''} by fit with your goal, strength of evidence, competitive gap and speed to act. Openings without evidence are left out.
                </p>
              </div>
            )}

            {phase === 'done' && (profile || competitors) && (
              <>
                <div className="gtm-sec">From first look to first customers</div>
                <div className="gtm-card gtm-cta gtm-fade">
                  <ul className="gtm-offer">
                    {FULL_STRATEGY.map(([title, line]) => (
                      <li key={title}>
                        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="7.5" fill="rgba(157,76,221,0.14)" stroke="rgba(157,76,221,0.55)" /><path d="M5 8.2l2 2 4-4.4" fill="none" stroke="#c9a6ff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        <div><b>{title}</b><span>{line}</span></div>
                      </li>
                    ))}
                  </ul>
                  <p className="gtm-p">This first look sees your market from the outside. A full strategy works from the inside: your customers, your pipeline and your numbers.</p>
                  <a className="gtm-btn" href={CALENDLY} target="_blank" rel="noopener noreferrer">Book an Intro Call</a>
                </div>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
